import React, { useState, useMemo, useEffect } from "react";
import { X, Building2, Search, Check, CheckSquare, Square } from "lucide-react";

/**
 * DepartmentPickerModal
 *
 * Props:
 *   show            – boolean
 *   onClose         – () => void
 *   theme           – "dark" | "light"
 *   isRTL           – boolean
 *   departments     – string[]  (all available department names)
 *   selectedDepts   – string[]  (currently selected)
 *   allSelected     – boolean   (true = "All Departments" mode)
 *   onConfirm       – ({ allDepartments: boolean, selectedDepartments: string[] }) => void
 */
const DepartmentPickerModal = ({
  show,
  onClose,
  theme,
  isRTL,
  departments = [],
  selectedDepts = [],
  allSelected = false,
  onConfirm,
}) => {
  const [search, setSearch] = useState("");
  const [localAll, setLocalAll] = useState(allSelected);
  const [localSelected, setLocalSelected] = useState(new Set(selectedDepts));

  /* Sync incoming props whenever modal opens */
  useEffect(() => {
    if (show) {
      setSearch("");
      setLocalAll(allSelected);
      setLocalSelected(new Set(selectedDepts));
    }
  }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () =>
      departments.filter((d) =>
        d.toLowerCase().includes(search.toLowerCase())
      ),
    [departments, search]
  );

  if (!show) return null;

  /* ── helpers ── */
  const toggleAll = () => {
    if (localAll) {
      setLocalAll(false);
      setLocalSelected(new Set());
    } else {
      setLocalAll(true);
      setLocalSelected(new Set());
    }
  };

  const toggleDept = (name) => {
    if (localAll) return; // while "All" is active, individual toggles are locked
    setLocalSelected((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const toggleFilteredAll = () => {
    if (localAll) return;
    const allFilteredSelected = filtered.every((d) => localSelected.has(d));
    setLocalSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filtered.forEach((d) => next.delete(d));
      } else {
        filtered.forEach((d) => next.add(d));
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm({
      allDepartments: localAll,
      selectedDepartments: localAll ? [] : Array.from(localSelected),
    });
    onClose();
  };

  /* ── derived ── */
  const checkedCount = localAll ? departments.length : localSelected.size;
  const allFilteredChecked =
    !localAll && filtered.length > 0 && filtered.every((d) => localSelected.has(d));

  /* ── styles ── */
  const isDark = theme === "dark";

  const overlay = {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.55)",
    backdropFilter: "blur(4px)",
    zIndex: 10100,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "16px",
  };

  const panel = {
    background: isDark ? "#1a2230" : "#ffffff",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "480px",
    maxHeight: "82vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 32px 72px rgba(0,0,0,0.45)",
    direction: isRTL ? "rtl" : "ltr",
    overflow: "hidden",
  };

  const headerBg = isDark ? "rgba(255,255,255,0.03)" : "#f0fdf4";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "#e5e7eb";
  const textPrimary = isDark ? "#f3f4f6" : "#111827";
  const textMuted = isDark ? "rgba(255,255,255,0.45)" : "#6b7280";
  const rowHover = isDark ? "rgba(255,255,255,0.04)" : "#f9fafb";
  const accentGreen = isDark ? "#34d399" : "#059669";
  const checkBg = isDark ? "rgba(52,211,153,0.15)" : "rgba(5,150,105,0.1)";

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{
          padding: "20px 24px 16px",
          background: headerBg,
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div style={{
                background: `linear-gradient(135deg, #059669, #047857)`,
                borderRadius: "10px", padding: "8px", display: "flex",
              }}>
                <Building2 size={18} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: "16px", fontWeight: "800", color: textPrimary }}>
                  {isRTL ? "اختيار الأقسام" : "Select Departments"}
                </div>
                <div style={{ fontSize: "12px", color: textMuted, marginTop: "2px" }}>
                  {checkedCount > 0
                    ? (isRTL ? `${checkedCount} قسم مختار` : `${checkedCount} selected`)
                    : (isRTL ? "لم يتم اختيار أي قسم" : "None selected")}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: textPrimary, padding: "6px", borderRadius: "8px",
                display: "flex", alignItems: "center",
              }}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* ── "All Departments" toggle ── */}
        <div style={{
          padding: "14px 24px",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={toggleAll}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "12px",
              border: `2px solid ${localAll ? accentGreen : (isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb")}`,
              background: localAll ? checkBg : "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "all 0.15s ease",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {/* custom checkbox */}
              <span style={{
                width: "20px", height: "20px",
                borderRadius: "6px",
                border: `2px solid ${localAll ? accentGreen : (isDark ? "rgba(255,255,255,0.25)" : "#d1d5db")}`,
                background: localAll ? accentGreen : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
                transition: "all 0.15s ease",
              }}>
                {localAll && <Check size={12} color="#fff" strokeWidth={3} />}
              </span>
              <span style={{ fontSize: "14px", fontWeight: "700", color: localAll ? accentGreen : textPrimary }}>
                {isRTL ? "جميع الأقسام" : "All Departments"}
              </span>
            </span>
            {localAll && (
              <span style={{
                fontSize: "11px", fontWeight: "700",
                color: accentGreen,
                background: checkBg,
                padding: "2px 10px",
                borderRadius: "999px",
              }}>
                {isRTL ? "مفعّل" : "Active"}
              </span>
            )}
          </button>
        </div>

        {/* ── Search + select-filtered-all row ── */}
        <div style={{
          padding: "12px 24px",
          borderBottom: `1px solid ${borderColor}`,
          flexShrink: 0,
          display: "flex",
          gap: "10px",
          alignItems: "center",
        }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search
              size={14}
              style={{
                position: "absolute",
                top: "50%",
                [isRTL ? "right" : "left"]: "11px",
                transform: "translateY(-50%)",
                color: "#9ca3af",
                pointerEvents: "none",
              }}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={isRTL ? "بحث في الأقسام..." : "Search departments..."}
              disabled={localAll}
              style={{
                width: "100%",
                padding: `9px 12px 9px ${isRTL ? "12px" : "34px"}`,
                paddingRight: isRTL ? "34px" : "12px",
                borderRadius: "10px",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb"}`,
                background: isDark ? "rgba(255,255,255,0.05)" : "#f9fafb",
                color: isDark ? "#fff" : "#111827",
                fontSize: "13px",
                outline: "none",
                boxSizing: "border-box",
                opacity: localAll ? 0.4 : 1,
                cursor: localAll ? "not-allowed" : "text",
              }}
            />
          </div>

          {/* select / deselect visible */}
          {!localAll && filtered.length > 0 && (
            <button
              type="button"
              onClick={toggleFilteredAll}
              title={allFilteredChecked
                ? (isRTL ? "إلغاء تحديد الكل" : "Deselect all visible")
                : (isRTL ? "تحديد الكل" : "Select all visible")}
              style={{
                flexShrink: 0,
                background: "none",
                border: `1px solid ${isDark ? "rgba(255,255,255,0.12)" : "#e5e7eb"}`,
                borderRadius: "8px",
                padding: "7px 10px",
                cursor: "pointer",
                color: allFilteredChecked ? accentGreen : textMuted,
                display: "flex", alignItems: "center", gap: "5px",
                fontSize: "12px", fontWeight: "600",
                whiteSpace: "nowrap",
              }}
            >
              {allFilteredChecked
                ? <><CheckSquare size={14} /> {isRTL ? "إلغاء الكل" : "Deselect all"}</>
                : <><Square size={14} /> {isRTL ? "تحديد الكل" : "Select all"}</>}
            </button>
          )}
        </div>

        {/* ── List ── */}
        <div style={{ overflowY: "auto", flex: 1, scrollbarWidth: "none" }}>
          {filtered.length === 0 ? (
            <div style={{
              padding: "40px 24px",
              textAlign: "center",
              color: textMuted,
              fontSize: "14px",
            }}>
              <Building2 size={32} style={{ marginBottom: "10px", opacity: 0.3 }} />
              <div>{isRTL ? "لا توجد أقسام" : "No departments found"}</div>
            </div>
          ) : (
            filtered.map((dept, idx) => {
              const isChecked = localAll || localSelected.has(dept);
              return (
                <button
                  key={dept}
                  type="button"
                  onClick={() => toggleDept(dept)}
                  disabled={localAll}
                  style={{
                    width: "100%",
                    padding: "13px 24px",
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    background: isChecked && !localAll ? (isDark ? "rgba(52,211,153,0.06)" : "rgba(5,150,105,0.04)") : "transparent",
                    border: "none",
                    borderBottom: idx < filtered.length - 1 ? `1px solid ${borderColor}` : "none",
                    cursor: localAll ? "default" : "pointer",
                    textAlign: isRTL ? "right" : "left",
                    direction: isRTL ? "rtl" : "ltr",
                    transition: "background 0.1s ease",
                  }}
                  onMouseEnter={(e) => {
                    if (!localAll) e.currentTarget.style.background = rowHover;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isChecked && !localAll
                      ? (isDark ? "rgba(52,211,153,0.06)" : "rgba(5,150,105,0.04)")
                      : "transparent";
                  }}
                >
                  {/* Checkbox */}
                  <span style={{
                    width: "18px", height: "18px", flexShrink: 0,
                    borderRadius: "5px",
                    border: `2px solid ${isChecked ? accentGreen : (isDark ? "rgba(255,255,255,0.25)" : "#d1d5db")}`,
                    background: isChecked ? accentGreen : "transparent",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s ease",
                  }}>
                    {isChecked && <Check size={11} color="#fff" strokeWidth={3} />}
                  </span>

                  <span style={{
                    fontSize: "14px",
                    fontWeight: isChecked ? "600" : "400",
                    color: isChecked && !localAll ? accentGreen : textPrimary,
                    flex: 1,
                    transition: "color 0.1s ease",
                  }}>
                    {dept}
                  </span>

                  {localAll && (
                    <span style={{ fontSize: "11px", color: accentGreen, fontWeight: "600", opacity: 0.7 }}>
                      {isRTL ? "مشمول" : "included"}
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: "16px 24px",
          borderTop: `1px solid ${borderColor}`,
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          flexShrink: 0,
          background: headerBg,
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "10px 22px", borderRadius: "10px",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.15)" : "#d1d5db"}`,
              background: "transparent",
              color: isDark ? "#fff" : "#374151",
              fontSize: "14px", fontWeight: "600", cursor: "pointer",
            }}
          >
            {isRTL ? "إلغاء" : "Cancel"}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={{
              padding: "10px 28px", borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg,#059669,#047857)",
              color: "#fff",
              fontSize: "14px", fontWeight: "700", cursor: "pointer",
              boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
            }}
          >
            {isRTL ? "تأكيد" : "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentPickerModal;